// API Client for VibeAtlas Web Platform
// Provides typed methods for all API interactions

import type { Pattern } from './supabase';
import { resilientFetch, FetchError, TIMEOUTS } from './resilient-fetch';

export interface SearchFilters {
  language?: string;
  category?: string;
  vertical?: string;
  sortBy?: 'trending' | 'newest' | 'popular' | 'fastest';
  limit?: number;
}

export interface ApiStats {
  totalPatterns: number;
  totalUsers: number;
  avgLatency: number;
  tokensSaved: number;
  moneySaved: number;
  topPatterns: Array<{
    id: string;
    name: string;
    category: string;
    uses: number;
    tokens_saved: number;
  }>;
}

export interface TrackingData {
  pattern_id: string;
  command: 'fix' | 'match' | 'explain';
  query_text?: string;
  tokens_saved?: number;
  confidence?: number;
  latency_ms?: number;
}

class APIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.NEXT_PUBLIC_API_URL || '/api';
  }

  private async fetchJSON<T>(
    endpoint: string,
    options?: RequestInit & { timeout?: number }
  ): Promise<T> {
    const response = await resilientFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      timeout: options?.timeout ?? TIMEOUTS.DEFAULT,
      retries: 3, // 3 retries
      retryDelay: 1000, // Start with 1 second delay (exponential backoff)
      onRetry: (attempt, error) => {
        console.warn(`API retry attempt ${attempt} for ${endpoint}:`, error.message);
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new FetchError(
        error.error || `HTTP ${response.status}`,
        response.status,
        false,
        false
      );
    }

    return response.json();
  }

  async getPatterns(filters?: SearchFilters): Promise<{ patterns: Pattern[] }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, String(value));
      });
    }
    const query = params.toString();
    return this.fetchJSON(`/patterns${query ? `?${query}` : ''}`);
  }

  async getPattern(id: string): Promise<{ pattern: Pattern }> {
    return this.fetchJSON(`/patterns/${id}`);
  }

  async searchPatterns(query: string, filters?: SearchFilters): Promise<{ patterns: Pattern[] }> {
    const params = new URLSearchParams({ q: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, String(value));
      });
    }
    return this.fetchJSON(`/search?${params.toString()}`);
  }

  async getStats(): Promise<ApiStats> {
    return this.fetchJSON('/stats');
  }

  async trackUsage(data: TrackingData): Promise<{ success: boolean }> {
    return this.fetchJSON('/track', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async installPattern(id: string, data: {
    project_path?: string;
    tool?: string;
  }): Promise<{ success: boolean }> {
    return this.fetchJSON(`/patterns/${id}/install`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const apiClient = new APIClient();
